import React from 'react';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Class as ClassIcon,
  FactCheck as ExamsIcon,
  Assignment as AssignmentIcon,
  AccountBalance as FinanceIcon,
  ChildCare as DaycareIcon,
  Inventory2 as InventoryIcon,
  Receipt as ExpensesIcon,
  FolderSpecial as DocReqIcon,
  EventBusy as LeaveIcon,
  Event as HolidayIcon,
  Campaign as AnnouncementsIcon,
  EventNote as MeetingsIcon,
  QuestionAnswer as QueriesIcon,
  BarChart as AnalyticsIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import HelpGuideLayout from '../../components/HelpGuideLayout';

const sections = [
  {
    id: 'dashboard',
    icon: DashboardIcon,
    title: 'Dashboard',
    content: [
      {
        heading: 'What you see',
        points: [
          'Live KPI counts for Teachers, Students, Classes and today’s Attendance.',
          'Finance overview, inventory low-stock alerts, and pending expense reimbursements, when those features are enabled.',
          'A 7-day attendance trend chart and a "Today at a Glance" summary.',
          'An onboarding checklist that walks a new school through first-time setup.',
        ],
      },
    ],
  },
  {
    id: 'people',
    icon: PeopleIcon,
    title: 'Teachers, Students & Staff',
    content: [
      {
        heading: 'Teachers',
        points: [
          'Add Teacher generates a unique employee ID and emails the teacher a password setup link — you never see or set their password directly.',
          'Edit a teacher’s profile, assigned classes/subjects, or reset their password (sends a new reset link) from Teachers > select teacher.',
          'View Work Hours shows check-in/check-out history recorded by each teacher.',
        ],
      },
      {
        heading: 'Students',
        points: [
          'Only Principals create students, classes, and teachers — this keeps academic records consistent across the school.',
          'Add Student auto-generates an admission number (ADM-YYYY-####) and, if you provide a parent email, creates the parent’s portal account and emails them login credentials.',
          'Open a student’s folder icon to manage their uploaded documents (birth certificate, transfer certificate, etc.).',
        ],
      },
      {
        heading: 'Non-Teaching Staff & School Admins',
        points: [
          'Non-Teaching Staff tracks support staff records separately from Teachers.',
          'School Admins are delegated accounts that can handle parent queries, daily updates and documents without full Principal access — manage them from School Admins.',
        ],
      },
    ],
  },
  {
    id: 'academics',
    icon: ClassIcon,
    title: 'Classes & Academics',
    featureKey: 'academics',
    content: [
      {
        heading: 'Always available',
        points: [
          'Classes: create/edit classes and assign a homeroom teacher. Classes stay available even if Academics is turned off for your school, since Daycare, Care Notes and Attendance depend on them.',
        ],
      },
      {
        heading: 'Exams, Marks & Report Cards',
        points: [
          'Manage Exams to create exam terms; teachers then enter marks against a specific exam (marks are unique per student + subject + exam).',
          'View Marks lets you review what teachers have entered across classes.',
          'Manage Report Cards configures grading rules and generates report cards for students.',
        ],
      },
      {
        heading: 'Weekly Plans & Calendar',
        points: [
          'Weekly Plans shows lesson plans teachers submit for the week ahead.',
          'Academic Calendar lets you add school events, exams and holidays in one unified calendar.',
          'Class Timetable lets you assign periods per class per day (Monday–Saturday).',
        ],
      },
    ],
  },
  {
    id: 'attendance',
    icon: AssignmentIcon,
    title: 'Attendance',
    content: [
      {
        heading: 'How it works',
        points: [
          'Attendance is always on (never gated by a feature flag) since other features depend on it.',
          'Teachers mark attendance per class; you can review and correct it from the Attendance page, organized by class in collapsible panels.',
          'The Dashboard flags classes where attendance hasn’t been marked yet for the day.',
        ],
      },
    ],
  },
  {
    id: 'finance',
    icon: FinanceIcon,
    title: 'Finance',
    content: [
      {
        heading: 'Fees & payments',
        points: [
          'Finance lets you set up student fee structures and record payments as they come in.',
          'Daycare charges are added to a student’s fees automatically every month based on logged daycare hours — you don’t need to enter them manually.',
        ],
      },
    ],
  },
  {
    id: 'daycare',
    icon: DaycareIcon,
    title: 'Daycare',
    content: [
      {
        heading: 'Setup & monitoring',
        points: [
          'Daycare Management has three tabs: Enrollments, Outstanding Dues, and Settings (hourly rate + assigned teacher).',
          'Only the assigned daycare teacher can check children in/out for daycare — set this in Settings first.',
          'If a teacher forgets to check a child out, an overnight job auto-closes the session and flags it; you’ll see a warning banner with a "Fix Time" option on the roster tab.',
        ],
      },
    ],
  },
  {
    id: 'inventory',
    icon: InventoryIcon,
    title: 'Inventory',
    featureKey: 'inventory',
    content: [
      {
        heading: 'Tracking stock',
        points: [
          'Inventory lets you log items (stationery, lab, sports, etc.) with quantity, unit cost, supplier and a reorder level.',
          'Use "Add Stock" / "Remove Stock" on an item to record stock movements — every movement is logged in its transaction history.',
          'When stock drops to or below the reorder level, you get an email + in-app alert and the item shows on the Dashboard’s low-stock panel.',
        ],
      },
    ],
  },
  {
    id: 'expenses',
    icon: ExpensesIcon,
    title: 'Expense Reimbursements',
    featureKey: 'expenses',
    content: [
      {
        heading: 'Reviewing teacher claims',
        points: [
          'Teachers submit reimbursement claims with amount, category, reason and date; you’re notified in-app when a new one arrives.',
          'Manage Expenses lets you Approve or Reject a pending claim (rejecting requires a note explaining why), then mark an approved claim as Paid once you’ve reimbursed it.',
          'Paid is final — it cannot be reopened or reversed.',
        ],
      },
    ],
  },
  {
    id: 'documents',
    icon: DocReqIcon,
    title: 'Student Documents',
    featureKey: 'student_documents',
    content: [
      {
        heading: 'Required documents & compliance',
        points: [
          'Required Documents lets you define which document types every student in your school must submit (birth certificate, Aadhaar, photo, etc.).',
          'Documents > Compliance shows a per-student matrix of what’s uploaded vs. missing.',
          'Open a student’s document folder to download, verify/reject, or delete files parents have uploaded — parents cannot delete their own uploads, only you can.',
        ],
      },
    ],
  },
  {
    id: 'leave',
    icon: LeaveIcon,
    title: 'Leave Management',
    featureKey: 'leave_management',
    content: [
      {
        heading: 'Approving teacher leave',
        points: [
          'Leave Management shows pending leave requests from teachers; approve or reject each one.',
          'Leave balance is only deducted when you approve a request — not when the teacher applies.',
        ],
      },
    ],
  },
  {
    id: 'holidays',
    icon: HolidayIcon,
    title: 'Holiday Calendar',
    content: [
      {
        heading: 'Managing holidays',
        points: [
          'Add school-wide holidays here; they automatically appear on the unified Academic Calendar and on teacher/parent calendars.',
        ],
      },
    ],
  },
  {
    id: 'announcements',
    icon: AnnouncementsIcon,
    title: 'Announcements',
    featureKey: 'announcements',
    content: [
      {
        heading: 'Broadcasting updates',
        points: [
          'Post school-wide announcements that teachers and (where relevant) parents will see in their dashboards and notifications.',
        ],
      },
    ],
  },
  {
    id: 'meetings',
    icon: MeetingsIcon,
    title: 'Parent Meetings',
    content: [
      {
        heading: 'Scheduling & follow-up',
        points: [
          'Parent Meetings lets you schedule meetings with parents, reschedule or cancel them (parents are emailed automatically either way).',
          'Marking a meeting "Completed" requires you to add notes — this is enforced so there’s always a record of what was discussed.',
        ],
      },
    ],
  },
  {
    id: 'queries',
    icon: QueriesIcon,
    title: 'Parent Queries',
    featureKey: 'parent_queries',
    content: [
      {
        heading: 'Handling parent questions',
        points: [
          'Parents raise queries from their portal; you (or staff/school admins) can pick them up, mark "In Progress", and then "Complete" with a resolution note — this notifies the parent by app, email and WhatsApp.',
          'Only Principals and School Admins can void a query if it was raised in error.',
        ],
      },
    ],
  },
  {
    id: 'analytics',
    icon: AnalyticsIcon,
    title: 'Analytics',
    content: [
      {
        heading: 'School-wide insights',
        points: [
          'Analytics gives you charts on attendance trends, academic performance, and other school-wide metrics.',
        ],
      },
    ],
  },
  {
    id: 'admins',
    icon: AdminIcon,
    title: 'School Admins',
    content: [
      {
        heading: 'Delegating access',
        points: [
          'School Admins is Principal-only: add staff accounts that can act on parent queries, daily updates and documents on your behalf without giving them full Principal access.',
        ],
      },
    ],
  },
];

const PrincipalHelpGuide = () => (
  <HelpGuideLayout
    title="How to Use ClassTree — Principal Guide"
    subtitle="A quick reference for everything you can do as a Principal. Sections for features your school hasn't enabled are hidden automatically."
    sections={sections}
  />
);

export default PrincipalHelpGuide;
